import { PROPERTY_TYPE_MULTI_SELECT, PROPERTY_TYPE_SELECT, PROPERTY_TYPE_STATUS } from 'shared/services/notion-constants';
import { addDoc, collection, deleteDoc, doc, onSnapshot, query, setDoc } from 'shared/services/firebase';
import { computed, ref } from 'vue';
import { intersection } from 'lodash-es';
import { v4 } from 'uuid';

export default function useTemplate(db, user, subscriptionManager) {
  const templates = ref({});
  let workspace = {};
  const loading = ref(true);

  const extractOptions = (property) => {
    let optionsNotion = [];
    if (['select', 'status', 'multi_select'].includes(property.type)) {
      optionsNotion = property[property.type].options.map((x) => ({ text: x.name, value: x.name, color: x.color }));
    }

    return optionsNotion;
  };

  const getPrefill = (property) => {
    if (property.type === 'url') {
      return 'url';
    }

    return 'manual';
  };

  const parseProperties = (properties) => {
    const res = {};
    const possibleProperties = ['title', 'rich_text', 'number', 'select', 'multi_select', 'date', 'url', 'checkbox', 'email', 'phone_number', 'status'];

    Object.keys(properties).forEach((propertyKey) => {
      const property = properties[propertyKey];
      const options = extractOptions(property);
      if (possibleProperties.includes(property.type)) {
        const shared = {
          id: property.id,
          type: property.type,
          options,
          prefill: getPrefill(property),
          query: '',
          predefined: [],
          checked: false,
          numTime: 0,
          unityTime: 'day',
          signTime: 1,
          endTime: false,
        };
        res[property.id] = { ...shared, name: property.name, endDate: { ...shared, name: `${property.name} - end` } };
      }
    });
    return res;
  };

  const writeDefaultTemplate = (database) => ({
    database,
    name: `Template for ${database.name}`,
    properties: parseProperties(database.properties),
    blocks: [],
  });

  const updateProperties = (oldProperties, database) => {
    const oldPs = JSON.parse(JSON.stringify(oldProperties));
    const newPs = parseProperties(database.properties);
    const updatedPs = {};

    Object.keys(newPs).forEach((key) => {
      const old = oldPs[key];
      if (old === undefined) {
        updatedPs[key] = newPs[key];
        return;
      }

      old.name = newPs[key].name;
      old.type = newPs[key].type;
      old.options = newPs[key].options;
      old.endDate.name = newPs[key].endDate.name;
      old.endDate.type = newPs[key].endDate.type;
      old.endDate.options = newPs[key].endDate.options;
      if ([PROPERTY_TYPE_MULTI_SELECT, PROPERTY_TYPE_SELECT, PROPERTY_TYPE_STATUS].includes(newPs[key].type)) {
        old.predefined = intersection(old.predefined, old.options.map((x) => x.value));
      }
      updatedPs[key] = old;
    });

    return updatedPs;
  };

  const init = (newWorkspace) => {
    loading.value = true;
    workspace = newWorkspace;
    const q = query(collection(db, 'users', user.value.uid, 'workspaces', workspace.id, 'templates'));
    const unsubscribe = onSnapshot(q, (querySnapshot) => {
      templates.value = {};
      querySnapshot.forEach((doc) => {
        const data = doc.data();
        if (data.database.archived === false) {
          templates.value[doc.id] = {
            id: doc.id,
            name: data.name,
            database: data.database,
            properties: updateProperties(data.properties, data.database),
            blocks: data.blocks === undefined ? [] : data.blocks,
          };
        }
      });
    });
    subscriptionManager.addSubscriber(unsubscribe, { auth: true });
    loading.value = false;
  };

  const getAll = () => computed(() => Object.values(templates.value));

  const getSingle = (id) => {
    if (tmpTemplate.value.id === id) {
      return tmpTemplate.value;
    }

    return templates.value[id];
  };

  const deleteSingle = async (id) => {
    const docRef = doc(db, 'users', user.value.uid, 'workspaces', workspace.id, 'templates', id);
    await deleteDoc(docRef);
  };

  const tmpTemplate = ref({});
  const addTmpTemplate = (data) => {
    const id = v4();
    tmpTemplate.value = { id, ...data };
    return id;
  };

  const addSingle = async (data) => {
    const colRef = collection(db, 'users', user.value.uid, 'workspaces', workspace.id, 'templates');
    const subdocRef = await addDoc(colRef, data);
    return subdocRef.id;
  };

  const updateSingle = (id, data) => {
    const docRef = doc(db, 'users', user.value.uid, 'workspaces', workspace.id, 'templates', id);
    setDoc(docRef, data);
  };
  const selectedTemplateId = ref(null);
  const selectTemplate = (id) => {
    selectedTemplateId.value = id;
  };

  const selectedTemplate = computed(() => getSingle(selectedTemplateId.value));

  return {
    init,
    getAll,
    getSingle,
    deleteSingle,
    addSingle,
    tmpTemplate,
    addTmpTemplate,
    updateSingle,
    selectTemplate,
    writeDefaultTemplate,
    selectedTemplate,
    loading,
  };
}
